import React from "react";
import { Provider } from "react-redux";

import * as Sentry from "@sentry/react";
import { ConnectedRouter } from "connected-react-router";
import { createBrowserHistory } from "history";
import { createRoot } from "react-dom/client";
import { setGlobalDevModeChecks } from "reselect";

import ErrorFallback from "@app/component/ErrorFallback";
import Environment from "@app/enum/Environment";

import configService from "./config/ConfigService";
import * as serviceWorker from "./serviceWorker";

// store be imported before other code to avoid runtime errors
// eslint-disable-next-line perfectionist/sort-imports
import reduxStore from "./store";
import polyfill from "./util/polyfills";
// eslint-disable-next-line perfectionist/sort-imports
import { basepath } from "@app/enum/Path";

import "./styles/index.scss";

import { applyVoiceTunableOverrides } from "@app/util/chat/voice/consts";
import { TOUR_ACTIVE_ATTRIBUTE_NAME } from "@app/util/consts";
import { getLocalTimeZone } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";
import getAppVersionInfo from "@app/util/getAppVersionInfo";
import { parseApiDateWithTz } from "@app/util/parsers";
import {
  checkIfOpenedFromAnnouncementCta,
  checkIfOpenedFromMobileApp,
  logoutIfAskedFor,
  readTokenFromUrlIfPresent,
  rememberHandoffReturn,
  rememberRedirectLink,
  watchCrossTabAuthChanges,
} from "@app/util/startup";

Sentry.init({
  dsn: configService.getSentryDsn(),
  environment: configService.getCurrentEnvironment(),
  integrations: [Sentry.browserTracingIntegration()],
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    /^https:\/\/localhost/,
    /^https:\/\/[^.]+\.mapmycustomers\.io/,
    /^https:\/\/[^.]+\.mapmycustomers\.co/,
    /^https:\/\/app2\.mapmycustomers\.me/,
    /^https:\/\/boundaries-api\.mapmycustomers\.me/,
    /^https:\/\/api-[^.]+\.mapmycustomers\.com/,
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
});

// Runs before the history reads the URL, so the router never sees the param.
checkIfOpenedFromAnnouncementCta();

const history = createBrowserHistory({ basename: basepath });
reduxStore.createStore(history);

// Enable selector stability checks on dev env to ensure input selectors return
// same instance on every call.
// For more info: https://reselect.js.org/api/development-only-stability-checks/
if (configService.getCurrentEnvironment() === Environment.DEVELOPMENT) {
  setGlobalDevModeChecks({ inputStabilityCheck: "once" });
}

const render = () => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const App = require("./App").default;

  const container = document.getElementById("root");
  const root = createRoot(container!);

  root.render(
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <Provider store={reduxStore.store}>
        <ConnectedRouter history={history}>
          <App />
        </ConnectedRouter>
      </Provider>
    </Sentry.ErrorBoundary>
  );
};

// this is needed to capture mousedown/pointerdown/contextmenu events BEFORE antd captures them and
// to prevent tooltips/popovers closing when data tour is active
// This is how Antd implements this: https://github.com/react-component/trigger/blob/master/src/hooks/useWinClick.ts#L44-L46
const eventInterceptor = (e: Event) => {
  if (document.body.hasAttribute(TOUR_ACTIVE_ATTRIBUTE_NAME)) {
    e.preventDefault();
    e.stopPropagation();
  }
};

window.addEventListener("mousedown", eventInterceptor, true);
window.addEventListener("contextmenu", eventInterceptor, true);
window.addEventListener("pointerdown", eventInterceptor, true);

polyfill().then(async () => {
  applyVoiceTunableOverrides(window.location.search);
  fetch("https://mapmycustomers.com/wp-content/uploads/2022/08/logo.svg")
    .then((response) => response.blob())
    .then((blob) => {
      const reader = new FileReader();
      const promise = new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
      });
      reader.readAsDataURL(blob);
      return promise;
    })
    .then((dataUrl) => {
      console.log(
        "%c                           ",
        `margin-top: 10px; font-size:100px; background:url(${dataUrl}) no-repeat;`
      );
    })
    .finally(() => {
      const { buildDate, version } = getAppVersionInfo();
      const date = parseApiDateWithTz(buildDate, getLocalTimeZone());
      console.log(`%c👋 v.${version} @ ${formatDate(date, "Pp")}`, "font-size: 64px;");
    });
  await readTokenFromUrlIfPresent();
  logoutIfAskedFor();
  rememberRedirectLink();
  rememberHandoffReturn();
  checkIfOpenedFromMobileApp();
  watchCrossTabAuthChanges();
  render();
});

if (process.env.NODE_ENV === "development" && module.hot) {
  module.hot.accept("./App", render);
}

// This error is logged on dev only and is because of the 3rd-party
// dep, so it's more annoying than informative. Hiding it here.
if (process.env.NODE_ENV === "development") {
  const originalError = console.error;
  console.error = (...args) => {
    if (typeof args[0] === "string" && args[0].includes("findDOMNode is deprecated")) {
      return;
    }
    originalError(...args);
  };
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
